import { SUPPORT_EMAIL } from '@/consts/app.const';
import logoImg from '@assets/images/logo.svg';
import { useAuth0 } from '@auth0/auth0-react';
import { IconOutlineLogout } from '@components/icons/outline';
import { Button, DoubleBounce } from '@components/ui';
import { useEffect } from 'react';

type Props = {
  error?: string;
};

const AUTO_LOGOUT_ERRORS = ['access_denied'];

export const AuthErrorScreen = ({ error }: Props) => {
  const { logout } = useAuth0();

  const shouldAutoLogout = error && AUTO_LOGOUT_ERRORS.includes(error);

  async function handleLogout() {
    await logout();
  }

  useEffect(() => {
    if (shouldAutoLogout) {
      setTimeout(async () => {
        await handleLogout();
      }, 2000);
    }
  }, [shouldAutoLogout, handleLogout]);

  return (
    <main className="flex min-h-screen flex-col items-center gap-20 p-8 text-center">
      <div className="shrink-0 py-2">
        <img
          className="mx-auto block h-10 xl:h-12"
          src={logoImg}
          alt="logo"
        />
      </div>

      <div className="grow">
        {shouldAutoLogout ? (
          <>
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <h2 className="mb-2 text-2xl font-semibold text-primary">
                Access Denied
              </h2>
              <p className="text-lg text-gray-700">
                Sorry, you don’t have access to the{' '}
                <span className="font-medium">Camu Dashboard App</span>.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                You’ll be redirected to the login screen shortly...
              </p>
            </div>

            <div className="mt-8 text-center text-secondary">
              <DoubleBounce size={100} />
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <h2 className="mb-2 text-2xl font-semibold text-primary">
                Unexpected Error
              </h2>
              <p className="text-lg text-gray-700">
                Please try logging out and logging back in. If the issue
                persists, contact Camu Support.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Need help? Reach out at{' '}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-info underline"
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </div>

            <div className="mt-12">
              <Button
                size="lg"
                className="!font-bold"
                prependIcon={IconOutlineLogout}
                rounded
                onClick={handleLogout}
              >
                Logout
              </Button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};
